import type { CombinedError, UseMutationResponse } from '@urql/vue';
import { computed, type ComputedRef } from 'vue';
import type { InfrastructureCommand } from './infrastructure-command';
import type { CommandBuilder } from './command-builder';

export class GraphqlCommandBuilder<
  Result,
  Args extends Record<string, unknown>,
  Mapped = Result,
  Error = { message: string },
> implements CommandBuilder<Result, Args, Mapped, Error> {
  static fromMutation<T, V extends Record<string, unknown>>(
    mutation: UseMutationResponse<T, V>,
    args: V,
  ): GraphqlCommandBuilder<T, V, void> {
    const voidMapper: () => void = () => undefined;
    return new GraphqlCommandBuilder(mutation, args, voidMapper);
  }

  private fetchingFn: (fetching: ComputedRef<boolean>) => ComputedRef<boolean>;

  // its not pretty but this constructor is private
  // eslint-disable-next-line max-params
  private constructor(
    private mutation: UseMutationResponse<Result, Args>,
    private args: Args,
    private mapper: (result: Result) => Mapped,
    private sideeffect?: (args: {
      result: Result;
      reject: (reason: unknown) => void;
    }) => void,
    private errorMapper?: (error: CombinedError) => Error,
  ) {
    this.fetchingFn = (fetching: ComputedRef<boolean>) => fetching;
  }

  withMapper<M>(
    mapper: (result: Result) => M,
  ): GraphqlCommandBuilder<Result, Args, M> {
    return new GraphqlCommandBuilder<Result, Args, M>(
      this.mutation,
      this.args,
      mapper,
      this.sideeffect,
    );
  }

  withErrorMapper<E>(
    errorMapper: (error: CombinedError) => E,
  ): GraphqlCommandBuilder<Result, Args, Mapped, E> {
    return new GraphqlCommandBuilder(
      this.mutation,
      this.args,
      this.mapper,
      this.sideeffect,
      errorMapper,
    );
  }

  withFetching(
    fetchingFn: (fetching: ComputedRef<boolean>) => ComputedRef<boolean>,
  ): this {
    this.fetchingFn = fetchingFn;
    return this;
  }

  withSideeffect(
    sideeffect: (args: {
      result: Result;
      reject: (reason: unknown) => void;
    }) => void,
  ): this {
    this.sideeffect = sideeffect;
    return this;
  }

  build(): InfrastructureCommand<Mapped, Error> {
    const run = (args?: Args) =>
      new Promise<Mapped>((resolve, reject) => {
        this.mutation
          .executeMutation(args ?? this.args)
          .then((result) => {
            if (result.error) {
              reject(result.error);
              return;
            }

            const data = result.data;
            if (data) {
              this.sideeffect?.({ result: data, reject });
              const mapped = this.mapper(data);
              resolve(mapped);
            } else {
              throw new Error(
                `Unexpected nullish result, ${JSON.stringify(data)}`,
              );
            }
          })
          .catch((err: unknown) => reject(err));
      });

    const error = computed<Error | null>(() => {
      if (!this.mutation.error.value) {
        return null;
      }
      return this.errorMapper?.(this.mutation.error.value) ?? null;
    });

    const fetching = computed(() => this.mutation.fetching.value);

    return {
      fetching: computed(() => this.fetchingFn(fetching).value),
      error,
      run,
    };
  }
}
